import { ColumnTypes, LameHeadsColumnKey } from 'models'
import { LicensesType } from 'models/IUserData'
import { useCallback, useMemo, useState } from 'react'
import { selectPermissions } from 'store/authSlice'
import { useAppSelector } from 'store/hooks'
import { showMilkYieldUi } from 'utils/companyMilkFeatures'
import {
	initialLameHeadsColumns,
	LAME_HEADS_MAX_SELECTED_COLUMNS,
	LAME_HEADS_MIN_SELECTED_COLUMNS,
} from '../helper/lameHeadsColumns'

const cloneColumns = (columns: ColumnTypes[]) =>
	columns.map(column => ({ ...column }))

const isSameColumnView = (left: ColumnTypes[], right: ColumnTypes[]) =>
	JSON.stringify(
		left.map(({ key, isShowColumn }) => ({ key, isShowColumn })),
	) ===
	JSON.stringify(right.map(({ key, isShowColumn }) => ({ key, isShowColumn })))

export const useLameHeadsColumns = () => {
	const permissions = useAppSelector(selectPermissions)
	const { userData } = useAppSelector(state => state.userReducer)
	const canShowMilkYield = showMilkYieldUi(userData?.company?.id)
	const [columns, setColumns] = useState<ColumnTypes[]>(() =>
		cloneColumns(initialLameHeadsColumns),
	)

	const availableColumns = useMemo(() => {
		return columns.filter(col => {
			if (col.requiresMilkYield && !canShowMilkYield) {
				return false
			}
			if (col.requiresLicense === LicensesType.FATNESS) {
				return (
					Boolean(permissions?.[LicensesType.FATNESS]) &&
					!userData.isFatnessExpired
				)
			}
			if (col.requiresLicense) {
				return permissions?.[col.requiresLicense] === true
			}
			return true
		})
	}, [canShowMilkYield, columns, permissions, userData.isFatnessExpired])

	const selectedCount = useMemo(
		() => availableColumns.filter(column => column.isShowColumn).length,
		[availableColumns],
	)

	const defaultAvailableColumns = useMemo(() => {
		return cloneColumns(initialLameHeadsColumns).filter(col => {
			if (col.requiresMilkYield && !canShowMilkYield) {
				return false
			}
			if (col.requiresLicense === LicensesType.FATNESS) {
				return (
					Boolean(permissions?.[LicensesType.FATNESS]) &&
					!userData.isFatnessExpired
				)
			}
			if (col.requiresLicense) {
				return permissions?.[col.requiresLicense] === true
			}
			return true
		})
	}, [canShowMilkYield, permissions, userData.isFatnessExpired])

	const isDefaultView = useMemo(
		() => isSameColumnView(availableColumns, defaultAvailableColumns),
		[availableColumns, defaultAvailableColumns],
	)

	const getColumn = useCallback(
		(key: LameHeadsColumnKey) => {
			return availableColumns.find(col => col.key === key)
		},
		[availableColumns],
	)

	const isColumnAvailable = useCallback(
		(col: ColumnTypes) => {
			if (col.requiresMilkYield && !canShowMilkYield) return false
			if (col.requiresLicense === LicensesType.FATNESS) {
				return (
					Boolean(permissions?.[LicensesType.FATNESS]) &&
					!userData.isFatnessExpired
				)
			}
			if (col.requiresLicense) {
				return permissions?.[col.requiresLicense] === true
			}
			return true
		},
		[canShowMilkYield, permissions, userData.isFatnessExpired],
	)

	const toggleColumnByTitle = useCallback(
		(title: string) => {
			setColumns(prevColumns => {
				const target = prevColumns.find(col => col.title === title)
				if (!target || !isColumnAvailable(target)) return prevColumns

				const visibleCount = prevColumns.filter(
					col => col.isShowColumn && isColumnAvailable(col),
				).length

				if (
					target.isShowColumn &&
					visibleCount <= LAME_HEADS_MIN_SELECTED_COLUMNS
				) {
					return prevColumns
				}
				if (
					!target.isShowColumn &&
					visibleCount >= LAME_HEADS_MAX_SELECTED_COLUMNS
				) {
					return prevColumns
				}

				return prevColumns.map(col =>
					col.title === title
						? { ...col, isShowColumn: !col.isShowColumn }
						: col,
				)
			})
		},
		[isColumnAvailable],
	)

	const resetColumns = useCallback(() => {
		setColumns(cloneColumns(initialLameHeadsColumns))
	}, [])

	return {
		columns: availableColumns,
		getColumn,
		toggleColumnByTitle,
		resetColumns,
		isDefaultView,
		selectedCount,
		minSelected: LAME_HEADS_MIN_SELECTED_COLUMNS,
		maxSelected: LAME_HEADS_MAX_SELECTED_COLUMNS,
	}
}
